import React, { useState, useEffect } from 'react'
import CurrencyInput from '../../../shared/CurrencyInput'
import Tooltip from '../../../shared/Tooltip'
import FloatingLabelInput from '../../../shared/FloatingLabelInput'
import Calculator from '../../../shared/icons/Calculator'

const DEFAULT_ACCEPTANCE_DISCLOSURE = 'This acceptance is a communication from a debt collector'
const DEFAULT_COUNTEROFFER_DISCLOSURE = 'This counteroffer is a communication from a debt collector'

const WAIT_TIME_FOR_AUTO_ACTION = 500

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

// Add constant at the top with other constants
const MAX_COUNTEROFFERS = 5

const AcceptOrCounteroffer = ({ account, validFirstPaymentDate, onSuccess, autoAction }) => {
  const [selectedAction, setSelectedAction] = useState(
    account.latest_historical_offer?.first_payment_date >= validFirstPaymentDate
      ? 'accept'
      : 'counteroffer'
  )

  const [activeDropdown, setActiveDropdown] = useState(null)
  const [isDisabled, setIsDisabled] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [activeCounterofferId, setActiveCounterofferId] = useState(1)
  const [counteroffers, setCounteroffers] = useState({
    1: {
      total_amount: '',
      payments: '',
      first_payment_date: '',
      tiers: [],
      disclosures: [DEFAULT_COUNTEROFFER_DISCLOSURE, ''],
    },
  })

  const [lastPaymentAmountCents, setLastPaymentAmountCents] = useState(null)

  // Keep disclosures in their own state since they're shared
  const [disclosures, setDisclosures] = useState([
    selectedAction === 'accept' ? DEFAULT_ACCEPTANCE_DISCLOSURE : DEFAULT_COUNTEROFFER_DISCLOSURE,
    '',
  ])

  // Update disclosure default when action changes
  useEffect(() => {
    setDisclosures(prev => {
      const newDisclosures = [...prev]
      newDisclosures[0] =
        selectedAction === 'accept'
          ? DEFAULT_ACCEPTANCE_DISCLOSURE
          : DEFAULT_COUNTEROFFER_DISCLOSURE
      return newDisclosures
    })
  }, [selectedAction])

  const debouncedTiers = useDebounce(JSON.stringify(counteroffers[activeCounterofferId]), 500)

  // Add a unique row identifier
  const rowId = `action-row-${account.customer_document.id}`

  // Modify useEffect to run once on mount
  useEffect(() => {
    // Adjust textareas
    setTimeout(() => {
      document.querySelectorAll('textarea').forEach(adjustTextareaHeight)
    }, 0)
  }, []) // Empty dependency array - runs once on mount

  // Add useEffect for click outside handler
  useEffect(() => {
    const handleClickOutside = event => {
      const dropdowns = document.querySelectorAll(`#${rowId} .action-dropdown`)
      const buttons = document.querySelectorAll(`#${rowId} .action-button`)

      // Don't close if clicking on any button in this row
      if (Array.from(buttons).some(button => button.contains(event.target))) {
        return
      }

      // Close if clicking outside dropdowns
      if (!Array.from(dropdowns).some(dropdown => dropdown.contains(event.target))) {
        setTimeout(() => {
          setActiveDropdown(null)
        }, 0)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [rowId])

  const handleAcceptOffer = async event => {
    event.preventDefault()
    setIsLoading(true)
    setIsDisabled(true)

    if (!confirm('Are you sure you want to accept this offer?')) {
      setIsLoading(false)
      setIsDisabled(false)
      return
    }

    const response = await fetch('/collectors/api/accept_offer', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
      },
      body: JSON.stringify({
        customer_document_id: account.customer_document.id,
        disclosures: disclosures.filter(d => d.trim() !== ''),
      }),
    })
    const data = await response.json()
    if (data.success) {
      onSuccess?.(data)
      return data
    } else {
      alert(data.error || 'Error processing request')
      setIsLoading(false)
      setIsDisabled(false)
    }
  }

  const handleCounterOffer = async event => {
    event.preventDefault()
    setIsLoading(true)
    setIsDisabled(true)

    if (!confirm('Are you sure you want to submit these counteroffers?')) {
      setIsLoading(false)
      setIsDisabled(false)
      return
    }

    // Convert counteroffers to array format
    const offers = Object.values(counteroffers).map(offer => ({
      customer_document_id: account.customer_document.id,
      total_amount: offer.total_amount,
      first_payment_date: offer.first_payment_date,
      phases: offer.tiers.map((tier, index) => ({
        index,
        payment_amount_cents: Math.round(parseFloat(tier.total) * 100).toString(),
        iterations: parseInt(tier.payments),
      })),
      payments: offer.tiers.length === 0 ? offer.payments : undefined,
    }))

    const response = await fetch('/collectors/api/create_counteroffer', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
      },
      body: JSON.stringify({
        offers,
        customer_document_id: account.customer_document.id,
        disclosures: disclosures.filter(d => d.trim() !== ''), // Use shared disclosures
      }),
    })

    const data = await response.json()
    if (data.success) {
      onSuccess?.(data)
      return data
    } else {
      if (data.offer_index) {
        const errorMessage = `Offer ${data.offer_index} is invalid: ${data.errors.join(', ')}`
        alert(errorMessage)
        setIsLoading(false)
        setIsDisabled(false)
      }
    }
  }

  // Add form change handler
  const handleFormChange = e => {
    const { name, value } = e.target
    if (activeDropdown === 'counteroffer') {
      setCounteroffers(prev => ({
        ...prev,
        [activeCounterofferId]: {
          ...prev[activeCounterofferId],
          [name]: value,
        },
      }))
    }
  }

  // Add this new function to handle dropdown visibility
  const ensureDropdownVisible = () => {
    const tableContainer = document.getElementById('table-container')
    const dropdown = document.querySelector(`#${rowId} .action-dropdown`)

    if (tableContainer && dropdown) {
      const dropdownRect = dropdown.getBoundingClientRect()
      const containerRect = tableContainer.getBoundingClientRect()

      // Calculate if dropdown extends beyond visible area
      const overflowAmount = dropdownRect.bottom - containerRect.bottom + 20 // 20px buffer

      if (overflowAmount > 0) {
        const scrollOptions = {
          top: tableContainer.scrollTop + overflowAmount,
          behavior: 'smooth',
        }
        tableContainer.scroll(scrollOptions)
      }
    }
  }

  // Update handleDisclosureChange
  const handleDisclosureChange = (index, value) => {
    if (index === 0) return

    setDisclosures(prev => {
      const newDisclosures = [...prev]
      newDisclosures[index] = value

      if (index === newDisclosures.length - 1 && value.trim() !== '') {
        newDisclosures.push('')
      }

      return newDisclosures
    })

    setTimeout(ensureDropdownVisible, 0)
  }

  // Update adjustTextareaHeight to include scrolling
  const adjustTextareaHeight = textarea => {
    if (!textarea) return
    const originalHeight = textarea.style.height
    textarea.style.height = 'auto'
    textarea.style.height = `${textarea.scrollHeight}px`

    // Only trigger scroll if height actually changed
    if (originalHeight !== textarea.style.height) {
      setTimeout(ensureDropdownVisible, 0)
    }
  }

  // Update click handler for buttons
  const handleButtonClick = action => {
    // If clicking the same button that's already active, close it
    if (activeDropdown === action) {
      setActiveDropdown(null)
    } else {
      // If clicking a different button, switch to it immediately
      setActiveDropdown(action)

      // Scroll to make dropdown visible
      setTimeout(() => {
        document.querySelectorAll('textarea').forEach(adjustTextareaHeight)
        ensureDropdownVisible()
      }, 0)
    }
    setSelectedAction(action)
  }

  // Update handleDeleteDisclosure
  const handleDeleteDisclosure = index => {
    if (index === 0) return // Don't allow deletion of the first disclosure

    setDisclosures(prev => prev.filter((_, i) => i !== index))
    setTimeout(ensureDropdownVisible, 0)
  }

  const showAcceptButton =
    account.latest_historical_offer?.first_payment_date >= validFirstPaymentDate

  // Add this helper function near the top of the component
  const calculateBalanceRemaining = (totalAmount, tiers) => {
    if (!totalAmount || !tiers?.length) return '$0.00'

    const total = parseFloat(totalAmount.replace(/[^0-9.-]+/g, ''))
    const tiersTotal = tiers.reduce((sum, tier) => {
      const tierTotal = parseFloat(tier.total.replace(/[^0-9.-]+/g, '') || 0)
      const payments = parseInt(tier.payments || 0)
      return sum + tierTotal * payments
    }, 0)

    const difference = total - tiersTotal
    if (Math.abs(difference) < 0.01) return '$0.00'

    return difference.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  // Update calculateMissingField to use the current tier data directly
  const calculateMissingField = (tier, index) => {
    const activeOffer = counteroffers[activeCounterofferId]
    if (!activeOffer?.total_amount) return

    const totalAmount = parseFloat(activeOffer.total_amount.replace(/[^0-9.-]+/g, ''))

    // Calculate total of all tiers except current one
    const otherTiersTotal = activeOffer.tiers.reduce((sum, currentTier, currentIndex) => {
      if (currentIndex === index) return sum
      const tierTotal = parseFloat(currentTier.total?.replace(/[^0-9.-]+/g, '') || 0)
      const payments = parseInt(currentTier.payments || 0)
      return sum + tierTotal * payments
    }, 0)

    // Calculate remaining amount for this tier
    const remainingForTier = totalAmount - otherTiersTotal
    if (remainingForTier <= 0) return

    const newTiers = [...activeOffer.tiers]

    if (tier.payments && !tier.total) {
      const payments = parseInt(tier.payments)
      const totalPerPayment = remainingForTier / payments
      newTiers[index].total = totalPerPayment.toFixed(2)
    } else if (tier.total && !tier.payments) {
      const tierTotal = parseFloat(tier.total.replace(/[^0-9.-]+/g, ''))
      const payments = Math.ceil(remainingForTier / tierTotal)
      newTiers[index].payments = payments.toString()
    }

    setCounteroffers(prev => ({
      ...prev,
      [activeCounterofferId]: {
        ...prev[activeCounterofferId],
        tiers: newTiers,
      },
    }))
  }

  // Update isCalculatorEnabled to handle tab changes
  const isCalculatorEnabled = (tier, index) => {
    const activeOffer = counteroffers[activeCounterofferId]
    if (!activeOffer?.total_amount) return false

    // Calculate total of all tiers except current one
    const otherTiersTotal = activeOffer.tiers.reduce((sum, currentTier, currentIndex) => {
      if (currentIndex === index) return sum
      const tierTotal = parseFloat(currentTier.total?.replace(/[^0-9.-]+/g, '') || 0)
      const payments = parseInt(currentTier.payments || 0)
      return sum + tierTotal * payments
    }, 0)

    const totalAmount = parseFloat(activeOffer.total_amount.replace(/[^0-9.-]+/g, ''))
    const remainingForTier = totalAmount - otherTiersTotal

    if (remainingForTier <= 0) return false
    if ((!tier.total && !tier.payments) || (tier.total && tier.payments)) return false

    return (tier.total && !tier.payments) || (!tier.total && tier.payments)
  }

  useEffect(() => {
    const fetchLastPayment = async () => {
      if (!debouncedTiers) return

      const activeOffer = JSON.parse(debouncedTiers)

      // Don't fetch if conditions aren't met
      if (
        !activeOffer.total_amount ||
        (activeOffer.total_amount && !activeOffer.payments && activeOffer.tiers.length === 0) ||
        (activeOffer.tiers.length > 0 &&
          !activeOffer.tiers.every(
            tier => tier.total?.trim() !== '' && tier.payments?.trim() !== ''
          ))
      ) {
        setLastPaymentAmountCents(null)
        return
      }

      try {
        const body = {
          customer_document_id: account.customer_document.id,
          total_amount: activeOffer.total_amount,
          first_payment_date: activeOffer.first_payment_date,
          phases: activeOffer.tiers.map((tier, index) => ({
            index,
            payment_amount_cents: Math.round(parseFloat(tier.total) * 100).toString(),
            iterations: parseInt(tier.payments),
          })),
          payments: activeOffer.tiers.length === 0 ? activeOffer.payments : undefined,
        }

        const response = await fetch('/collectors/api/determine_offer_last_payment', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content,
          },
          body: JSON.stringify(body),
        })

        if (!response.ok) throw new Error('Network response was not ok')

        const data = await response.json()

        setLastPaymentAmountCents(
          data.success && data.has_extra_tier ? data.last_payment_amount_cents : null
        )
      } catch {
        setLastPaymentAmountCents(null)
      }
    }

    fetchLastPayment()
  }, [debouncedTiers, activeCounterofferId, account.customer_document.id])

  // Add useEffect for auto-action
  useEffect(() => {
    if (!autoAction) return

    // Check if customer_document_id exists in URL params
    const urlParams = new URLSearchParams(window.location.search)
    const hasCustomerDocumentId = urlParams.has('filters[customer_document_id]')
    if (!hasCustomerDocumentId) return

    const timer = setTimeout(() => {
      if (autoAction === 'accept' && showAcceptButton) {
        handleButtonClick('accept')
        // Use another timeout to ensure the dropdown is rendered before submitting
        setTimeout(() => {
          const form = document.querySelector(`#${rowId} form`)
          if (form) {
            handleAcceptOffer({ preventDefault: () => {} })
          }
        }, WAIT_TIME_FOR_AUTO_ACTION)
      } else if (autoAction === 'counteroffer') {
        handleButtonClick('counteroffer')
      }
    }, WAIT_TIME_FOR_AUTO_ACTION)

    return () => clearTimeout(timer)
  }, [autoAction, showAcceptButton]) // Only run when autoAction or showAcceptButton changes

  // Update addNewCounteroffer to use next available index
  const addNewCounteroffer = () => {
    if (Object.keys(counteroffers).length >= MAX_COUNTEROFFERS) return

    // Get sorted list of current offer IDs
    const currentIds = Object.keys(counteroffers)
      .map(Number)
      .sort((a, b) => a - b)

    // Find first gap in sequence or use next number
    let newId = 1
    for (const id of currentIds) {
      if (id !== newId) break
      newId++
    }

    setCounteroffers(prev => ({
      ...prev,
      [newId]: {
        total_amount: '',
        payments: '',
        first_payment_date: '',
        tiers: [],
        disclosures: [DEFAULT_COUNTEROFFER_DISCLOSURE, ''],
      },
    }))
    setActiveCounterofferId(newId)
  }

  const deleteCounteroffer = id => {
    if (Object.keys(counteroffers).length <= 1) return // Maintain minimum of 1

    setCounteroffers(prev => {
      const newOffers = { ...prev }
      delete newOffers[id]

      // Renumber remaining offers
      const sortedIds = Object.keys(newOffers)
        .map(Number)
        .sort((a, b) => a - b)
      const renumberedOffers = {}
      sortedIds.forEach((oldId, index) => {
        renumberedOffers[index + 1] = newOffers[oldId]
      })

      return renumberedOffers
    })

    // Update active ID after renumbering
    setActiveCounterofferId(prev => {
      if (id === prev) {
        // If deleting active tab, switch to first available
        return 1
      } else if (id < prev) {
        // If deleting tab before active, decrement active
        return prev - 1
      }
      return prev
    })
  }

  // Update the JSX for disclosures section (same for both accept and counteroffer forms)
  const renderDisclosures = () => (
    <div className='d-flex flex-column gap-2' style={{ minWidth: '400px' }}>
      <Tooltip
        width='320px'
        text='Enter all of your disclosures for this communication here. These will be displayed conspicuously to the consumer.'
        position='top'
      >
        <h4 style={{ marginBottom: '0px' }}>Disclosures</h4>
      </Tooltip>
      {disclosures.map((disclosure, index) => (
        <div key={`disclosure-${index}`} className='d-flex gap-2'>
          <FloatingLabelInput
            label={`Disclosure ${index + 1}`}
            value={disclosure}
            onChange={e => {
              handleDisclosureChange(index, e.target.value)
            }}
            isTextarea
            rows={1}
            readOnly={index === 0}
            style={{ flex: 1 }}
          />
          <button
            type='button'
            className='btn btn-link text-secondary p-0'
            onClick={() => handleDeleteDisclosure(index)}
            style={{
              minWidth: '24px',
              visibility:
                disclosures.length > 1 && disclosure.trim() !== '' && index !== 0
                  ? 'visible'
                  : 'hidden',
            }}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  )

  return (
    <div className='position-relative' id={rowId}>
      <div className='d-flex align-items-start'>
        <span className='position-relative'>
          {showAcceptButton && (
            <Tooltip text='Accept the offer' position='top'>
              <button
                type='button'
                className={`d-block w-100 border-0 px-3 py-2 text-start rounded-top-start-1 rounded-bottom-end-1 bg-success text-white action-button`}
                onClick={() => handleButtonClick('accept')}
              >
                Accept
              </button>
            </Tooltip>
          )}

          {/* Accept dropdown */}
          {activeDropdown === 'accept' && (
            <div
              className='bg-white shadow-lg border rounded-start-top-1 rounded-end-bottom-1 p-3 position-absolute action-dropdown'
              style={{
                top: '100%',
                right: 0,
                zIndex: 1000,
                marginTop: 2,
              }}
            >
              <form onSubmit={handleAcceptOffer} className='d-flex flex-column gap-2'>
                <div>
                  <button
                    type='submit'
                    className='btn btn-primary btn-sm'
                    disabled={isLoading || isDisabled}
                  >
                    {isLoading ? (
                      <>
                        <span
                          className='spinner-border spinner-border-sm me-1'
                          role='status'
                          aria-hidden='true'
                        ></span>
                        Submitting...
                      </>
                    ) : isDisabled ? (
                      'Submitted'
                    ) : (
                      'Accept offer'
                    )}
                  </button>
                </div>
                {renderDisclosures()}
              </form>
            </div>
          )}
        </span>

        {showAcceptButton && <span>&nbsp;</span>}

        <span className='position-relative'>
          <Tooltip text='Submit a counteroffer to the consumer' position='top'>
            <button
              type='button'
              className={`d-block w-100 border-0 px-3 py-2 text-start rounded-top-start-1 rounded-bottom-end-1 bg-warning text-white action-button`}
              onClick={() => handleButtonClick('counteroffer')}
            >
              Counteroffer
            </button>
          </Tooltip>

          {/* Counteroffer dropdown */}
          {activeDropdown === 'counteroffer' && (
            <div
              className='bg-white shadow-lg border rounded-start-top-1 rounded-end-bottom-1 position-absolute action-dropdown'
              style={{
                top: '100%',
                right: 0,
                zIndex: 1000,
                marginTop: 2,
                minWidth: '400px',
              }}
            >
              {/* Updated tabs styling */}
              <div
                className='d-flex position-relative'
                style={{ zIndex: 1001, borderColor: '#9E9E9E', border: 1, marginBottom: -1 }}
              >
                {Object.keys(counteroffers).map(id => (
                  <button
                    key={id}
                    type='button'
                    className={`px-3 py-2 ${
                      activeCounterofferId === Number(id)
                        ? 'bg-white'
                        : 'bg-light text-secondary border-end'
                    }`}
                    style={{
                      position: 'relative',
                      border: activeCounterofferId === Number(id) ? '1px solid #9E9E9E' : 'none',
                      borderBottom: activeCounterofferId === Number(id) ? '1px solid #fff' : 'none',
                      marginBottom: activeCounterofferId === Number(id) ? 0 : 2,
                    }}
                    onClick={() => setActiveCounterofferId(Number(id))}
                  >
                    Offer {id}
                    <span
                      className='ms-2 text-secondary'
                      onClick={e => {
                        e.stopPropagation()
                        deleteCounteroffer(Number(id))
                      }}
                      style={{
                        fontSize: '20px',
                        fontWeight: '300',
                        cursor: Object.keys(counteroffers).length > 1 ? 'pointer' : 'default',
                        position: 'relative',
                        top: '-1px',
                        visibility: Object.keys(counteroffers).length > 1 ? 'visible' : 'hidden',
                      }}
                    >
                      ×
                    </span>
                  </button>
                ))}
                <button
                  type='button'
                  className='border-0 px-3 py-2 bg-light text-secondary border-start'
                  style={{
                    transition: 'all 0.2s ease',
                    borderColor: '#dee2e6',
                    marginBottom: 2,
                  }}
                  onClick={addNewCounteroffer}
                >
                  +
                </button>
              </div>

              <div className='p-3' style={{ borderTop: '1px solid #9E9E9E' }}>
                <form onSubmit={handleCounterOffer} className='d-flex flex-column gap-2'>
                  {/* Use the active counteroffer's data */}
                  {counteroffers[activeCounterofferId].tiers.length === 0 ? (
                    <div className='d-flex gap-2'>
                      <div className='d-flex gap-2'>
                        <CurrencyInput
                          label='Total Amount'
                          name='total_amount'
                          value={counteroffers[activeCounterofferId].total_amount}
                          onChange={handleFormChange}
                          style={{ width: '125px' }}
                          required
                        />
                        <FloatingLabelInput
                          label='Payments'
                          type='number'
                          name='payments'
                          value={counteroffers[activeCounterofferId].payments}
                          onChange={handleFormChange}
                          style={{ width: '80px' }}
                          required
                        />
                        <FloatingLabelInput
                          label='First Payment'
                          type='date'
                          name='first_payment_date'
                          value={counteroffers[activeCounterofferId].first_payment_date}
                          onChange={handleFormChange}
                          required
                          min={validFirstPaymentDate}
                        />
                      </div>
                      <button
                        type='button'
                        className='btn btn-primary btn-sm'
                        onClick={() => {
                          setCounteroffers(prev => ({
                            ...prev,
                            [activeCounterofferId]: {
                              ...prev[activeCounterofferId],
                              tiers: [
                                { total: '', payments: '' },
                                { total: '', payments: '' },
                              ],
                            },
                          }))
                          setTimeout(ensureDropdownVisible, 0)
                        }}
                      >
                        + Tier
                      </button>
                      <button
                        type='submit'
                        className='btn btn-primary btn-sm'
                        disabled={isLoading || isDisabled}
                      >
                        {isLoading ? (
                          <>
                            <span
                              className='spinner-border spinner-border-sm me-1'
                              role='status'
                              aria-hidden='true'
                            ></span>
                            Submitting...
                          </>
                        ) : isDisabled ? (
                          'Submitted'
                        ) : Object.keys(counteroffers).length > 1 ? (
                          'Submit counteroffers'
                        ) : (
                          'Submit counteroffer'
                        )}
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* First row - Total amount and date when using tiers */}
                      <div className='d-flex gap-2'>
                        <CurrencyInput
                          label='Total Amount'
                          name='total_amount'
                          value={counteroffers[activeCounterofferId].total_amount}
                          onChange={handleFormChange}
                          style={{ width: '125px' }}
                          required
                        />
                        <FloatingLabelInput
                          label='First Payment'
                          type='date'
                          name='first_payment_date'
                          value={counteroffers[activeCounterofferId].first_payment_date}
                          onChange={handleFormChange}
                          required
                          min={validFirstPaymentDate}
                        />
                        <FloatingLabelInput
                          label='Remaining Balance'
                          type='text'
                          name='balance_remaining'
                          value={
                            lastPaymentAmountCents
                              ? '$0.00'
                              : calculateBalanceRemaining(
                                  counteroffers[activeCounterofferId].total_amount,
                                  counteroffers[activeCounterofferId].tiers
                                )
                          }
                          className={
                            calculateBalanceRemaining(
                              counteroffers[activeCounterofferId].total_amount,
                              counteroffers[activeCounterofferId].tiers
                            ) !== '$0.00' && !lastPaymentAmountCents
                              ? 'text-danger'
                              : ''
                          }
                          style={{ width: '125px' }}
                          disabled={true}
                        />
                        <button
                          type='submit'
                          className='btn btn-primary btn-sm'
                          disabled={isLoading || isDisabled}
                        >
                          {isLoading ? (
                            <>
                              <span
                                className='spinner-border spinner-border-sm me-1'
                                role='status'
                                aria-hidden='true'
                              ></span>
                              Submitting...
                            </>
                          ) : isDisabled ? (
                            'Submitted'
                          ) : Object.keys(counteroffers).length > 1 ? (
                            'Submit counteroffers'
                          ) : (
                            'Submit counteroffer'
                          )}
                        </button>
                      </div>

                      {/* Tier rows */}
                      {counteroffers[activeCounterofferId].tiers.map((tier, index) => (
                        <div key={index} className='d-flex gap-2 align-items-center'>
                          <CurrencyInput
                            label={`Tier ${index + 1} Total`}
                            name={`tiers.${index}.total`}
                            value={tier.total}
                            onChange={e => {
                              const newTiers = [...counteroffers[activeCounterofferId].tiers]
                              newTiers[index].total = e.target.value
                              setCounteroffers(prev => ({
                                ...prev,
                                [activeCounterofferId]: {
                                  ...prev[activeCounterofferId],
                                  tiers: newTiers,
                                },
                              }))
                            }}
                            style={{ width: '125px' }}
                            required
                          />
                          <div className='d-flex gap-2 align-items-center'>
                            <FloatingLabelInput
                              label='Payments'
                              type='number'
                              name={`tiers.${index}.payments`}
                              value={tier.payments}
                              onChange={e => {
                                const newTiers = [...counteroffers[activeCounterofferId].tiers]
                                newTiers[index].payments = e.target.value
                                setCounteroffers(prev => ({
                                  ...prev,
                                  [activeCounterofferId]: {
                                    ...prev[activeCounterofferId],
                                    tiers: newTiers,
                                  },
                                }))
                              }}
                              style={{ width: '80px' }}
                              required
                            />
                            <button
                              type='button'
                              className={`btn btn-link btn-sm ${isCalculatorEnabled(tier, index) ? 'text-primary' : 'text-secondary'}`}
                              disabled={!isCalculatorEnabled(tier, index)}
                              onClick={() => calculateMissingField(tier, index)}
                              style={{ padding: '4px 8px' }}
                            >
                              <Calculator />
                            </button>
                          </div>
                          <button
                            type='button'
                            className='btn btn-link text-secondary p-0'
                            onClick={() => {
                              setCounteroffers(prev => ({
                                ...prev,
                                [activeCounterofferId]: {
                                  ...prev[activeCounterofferId],
                                  tiers:
                                    prev[activeCounterofferId].tiers.length <= 2
                                      ? []
                                      : prev[activeCounterofferId].tiers.filter(
                                          (_, i) => i !== index
                                        ),
                                },
                              }))
                              setTimeout(ensureDropdownVisible, 0)
                            }}
                            style={{ minWidth: '24px' }}
                          >
                            ×
                          </button>
                          {index === counteroffers[activeCounterofferId].tiers.length - 1 && (
                            <button
                              type='button'
                              className='btn btn-primary btn-sm'
                              onClick={() => {
                                setCounteroffers(prev => ({
                                  ...prev,
                                  [activeCounterofferId]: {
                                    ...prev[activeCounterofferId],
                                    tiers: [
                                      ...prev[activeCounterofferId].tiers,
                                      { total: '', payments: '' },
                                    ],
                                  },
                                }))
                                setTimeout(ensureDropdownVisible, 0)
                              }}
                            >
                              + Tier
                            </button>
                          )}
                        </div>
                      ))}
                    </>
                  )}

                  {counteroffers[activeCounterofferId].tiers.length > 0 &&
                    lastPaymentAmountCents && (
                      <small className='text-muted'>
                        Final payment of tier {counteroffers[activeCounterofferId].tiers.length}{' '}
                        will be ${lastPaymentAmountCents / 100}
                      </small>
                    )}

                  {renderDisclosures()}
                </form>
              </div>
            </div>
          )}
        </span>
      </div>
    </div>
  )
}

export default AcceptOrCounteroffer
